import * as THREE from 'three';

// Compute a backward-offset preview when the mouse moves backward over the last segment.
// - vertices: array of THREE.Vector3 (existing vertices)
// - previewPt: THREE.Vector3 candidate preview point
// - mouseWorldPoint: actual mouse world position (used to determine side when backward starts)
// - backwardActiveRef: useRef boolean-like object { current: boolean }
// - backwardSideRef: useRef number-like object { current: -1|1 }
// - ctrlPressed: boolean - CTRL tuşu basılı mı (135 derece açı için)
// Returns { offsetPoint, projectedPoint } or null
export function computeBackwardProjection(vertices, previewPt, mouseWorldPoint, backwardActiveRef, backwardSideRef, options = {}) {
    const OFFSET = options.offset ?? 5;
    const THRESH = options.threshold ?? -0.0001;
    const ctrlPressed = options.ctrlPressed ?? false; // CTRL tuşu kontrolü

    if (!vertices || vertices.length < 2) return null;

    const len = vertices.length;
    const last = vertices[len - 1];
    const prev = vertices[len - 2];

    const dir = new THREE.Vector3().subVectors(last, prev);
    const dirLen = dir.length();
    if (dirLen === 0) return null;

    const dirNorm = dir.clone().normalize();
    const pVec = new THREE.Vector3().subVectors(previewPt, last);
    const proj = pVec.dot(dirNorm);

    // Not moving backwards
    if (proj > THRESH) {
        backwardActiveRef.current = false;
        backwardSideRef.current = 0;
        return null;
    }

    // Determine side when backward starts, and lock it. Use mouseWorldPoint when available.
    let side = backwardSideRef.current;
    if (!backwardActiveRef.current) {
        const refPoint = mouseWorldPoint || previewPt;
        const mVec = new THREE.Vector3().subVectors(refPoint, last);
        const crossZ = dirNorm.x * mVec.y - dirNorm.y * mVec.x;
        side = Math.sign(crossZ);
        if (side === 0) {
            const perp = new THREE.Vector3(-dirNorm.y, dirNorm.x, 0);
            side = Math.sign(perp.dot(mVec)) || 1;
        }
        backwardSideRef.current = side || 1;
        backwardActiveRef.current = true;
     } else {
        // If already active, switch side immediately if mouse crosses
        const refPointNow = mouseWorldPoint || previewPt;
        const mVecNow = new THREE.Vector3().subVectors(refPointNow, last);
        const crossNow = dirNorm.x * mVecNow.y - dirNorm.y * mVecNow.x;
        let curSide = Math.sign(crossNow);
        if (curSide === 0) {
            const perpNow = new THREE.Vector3(-dirNorm.y, dirNorm.x, 0);
            curSide = Math.sign(perpNow.dot(mVecNow)) || 1;
        }
        if (curSide !== backwardSideRef.current) {
            backwardSideRef.current = curSide;
         }
        side = backwardSideRef.current || 1;
    }

    // ✅ CTRL basılı ise 135 derece açı, değilse 90 derece (normal)
    if (ctrlPressed) {
        // 135 derece açı ile ilk kenar, sonra paralel geri dönüş
        // 135 derece = 90 derece + 45 derece
        // dirNorm yönünde geri giderken, 135 derece açıyla yukarı/aşağı sapmalı
        
        // Perpendicular yön (90 derece)
        const perp = new THREE.Vector3(-dirNorm.y, dirNorm.x, 0);
        
        // 135 derece için: -dirNorm (180 derece) ile perp (90 derece) ortası = 135 derece
        // 135 derece = -45 derece sapma (geriye doğru)
        // Yön: perp yönünde OFFSET, sonra dirNorm yönünde -OFFSET (45 derece sapma)
        
        const angle135 = Math.PI * 3 / 4; // 135 derece radyan
        
        // dirNorm'u 135 derece döndür (side'a göre)
        const cos135 = Math.cos(angle135 * side);
        const sin135 = Math.sin(angle135 * side);
        
        // Rotation matrix: [cos -sin] [dirNorm.x]
        //                  [sin  cos] [dirNorm.y]
        const rotatedDir = new THREE.Vector3(
            dirNorm.x * cos135 - dirNorm.y * sin135,
            dirNorm.x * sin135 + dirNorm.y * cos135,
            0
        );
        
        // ✅ 135 derece açıda hipotenüs: OFFSET * √2 = 5 * 1.414... ≈ 7.07
        const diagonal135 = OFFSET * Math.SQRT2;
        
        // İlk offset noktası: 135 derece açıyla diagonal135 uzaklıkta
        const firstOffsetPoint = last.clone().add(rotatedDir.clone().multiplyScalar(diagonal135));
        
        // İkinci nokta: İlk offset noktasından dirNorm yönünde (paralel) proj kadar
        const projectedPoint = firstOffsetPoint.clone().add(dirNorm.clone().multiplyScalar(proj));
        
        return { 
            offsetPoint: firstOffsetPoint, 
            projectedPoint,
            is135Mode: true // 135 derece modu aktif
        };
    } else {
        // Normal 90 derece mod
        const perp = new THREE.Vector3(-dirNorm.y, dirNorm.x, 0);
        const offsetPoint = last.clone().add(perp.clone().multiplyScalar(OFFSET * side));
        const projectedPoint = offsetPoint.clone().add(dirNorm.clone().multiplyScalar(proj));
        return { offsetPoint, projectedPoint };
    }
}
